import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["ref"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import mergeRefs from 'merge-refs';
import { useRef, useState } from 'react';
import { DATA_SWIPE_DIRECTIONS_ATTRIBUTE, useSwipeable } from '@snack-uikit/utils';
import { POSITION_TO_SWIPE_DIRECTION_MAP, SWIPE_DIRECTION_TO_POSITION_MAP } from './constants';
var TRANSFORM = 0;
var SWIPE_DURATION = 500;
export function useSwipeProps(_ref) {
  var _ref2, _swipeRef$current, _swipeRef$current2;
  var onSwiped = _ref.onSwiped,
    position = _ref.position,
    enabled = _ref.enabled;
  var swipeRef = useRef(null);
  var canCloseDrawer = useRef(true);
  var itemSize = (_ref2 = position === 'bottom' || position === 'top' ? (_swipeRef$current = swipeRef.current) === null || _swipeRef$current === void 0 ? void 0 : _swipeRef$current.offsetHeight : (_swipeRef$current2 = swipeRef.current) === null || _swipeRef$current2 === void 0 ? void 0 : _swipeRef$current2.offsetWidth) !== null && _ref2 !== void 0 ? _ref2 : 0;
  var swipeStart = useRef(0);
  var getInitialDrag = function getInitialDrag() {
    return {
      initial: TRANSFORM,
      start: 0,
      isDown: false,
      drag: 0,
      finished: true,
      pointers: true
    };
  };
  var _useState = useState(getInitialDrag),
    _useState2 = _slicedToArray(_useState, 2),
    drag = _useState2[0],
    setDrag = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    styles = _useState4[0],
    setStyles = _useState4[1];
  var getDrawerTransform = function getDrawerTransform(value) {
    switch (position) {
      case 'bottom':
      case 'top':
        return "translateY(".concat(TRANSFORM - value, "px)");
      case 'right':
      case 'left':
      default:
        return "translateX(".concat(TRANSFORM - value, "px)");
    }
  };
  var getMaskOpacity = function getMaskOpacity(value) {
    return 1 + value / itemSize;
  };
  var resetStyles = function resetStyles() {
    setStyles(undefined);
    setDrag(getInitialDrag());
  };
  var _isContainedInArea = function isContainedInArea(_ref3) {
    var element = _ref3.element,
      condition = _ref3.condition;
    if (!element) {
      return false;
    }
    if (element === swipeRef.current) {
      return false;
    }
    if (!element.parentElement) {
      return false;
    }
    return condition(element) || _isContainedInArea({
      element: element.parentElement,
      condition: condition
    });
  };
  var isSwipeEnabled = function isSwipeEnabled(eventData) {
    var element = eventData.event.target;
    var equalDirectionsCondition = function equalDirectionsCondition(el) {
      var _el$getAttribute$spli, _el$getAttribute;
      var directions = (_el$getAttribute$spli = (_el$getAttribute = el.getAttribute(DATA_SWIPE_DIRECTIONS_ATTRIBUTE)) === null || _el$getAttribute === void 0 ? void 0 : _el$getAttribute.split(' ')) !== null && _el$getAttribute$spli !== void 0 ? _el$getAttribute$spli : [];
      return directions.some(function (direction) {
        return direction === eventData.dir;
      });
    };
    if (_isContainedInArea({
      element: element,
      condition: equalDirectionsCondition
    })) {
      return false;
    }
    return position === SWIPE_DIRECTION_TO_POSITION_MAP[eventData.dir];
  };
  var handleSwipeStart = function handleSwipeStart() {
    swipeStart.current = Date.now();
  };
  var handleSwiping = function handleSwiping(eventData) {
    if (!isSwipeEnabled(eventData)) {
      return;
    }
    if (!canCloseDrawer.current) {
      return;
    }
    var adjustedDrag = 0;
    var element = eventData.event.target;
    switch (position) {
      case 'left':
        if (_isContainedInArea({
          element: element,
          condition: function condition(el) {
            return el.scrollWidth - el.offsetWidth > el.scrollLeft;
          }
        })) {
          canCloseDrawer.current = false;
          return;
        }
        adjustedDrag = Math.max(0, -eventData.deltaX);
        break;
      case 'right':
        if (_isContainedInArea({
          element: element,
          condition: function condition(el) {
            return el.scrollLeft > 0;
          }
        })) {
          canCloseDrawer.current = false;
          return;
        }
        adjustedDrag = Math.min(0, -eventData.deltaX);
        break;
      case 'top':
        if (_isContainedInArea({
          element: element,
          condition: function condition(el) {
            return el.scrollHeight - el.offsetHeight > el.scrollTop;
          }
        })) {
          canCloseDrawer.current = false;
          return;
        }
        adjustedDrag = Math.max(0, -eventData.deltaY);
        break;
      case 'bottom':
        if (_isContainedInArea({
          element: element,
          condition: function condition(el) {
            return el.scrollTop > 0;
          }
        })) {
          canCloseDrawer.current = false;
          return;
        }
        adjustedDrag = Math.min(0, -eventData.deltaY);
        break;
      default:
        break;
    }
    setDrag(function (prevDrag) {
      return _objectSpread(_objectSpread({}, prevDrag), {}, {
        drag: adjustedDrag,
        pointers: eventData.absX < Number.MIN_SAFE_INTEGER
      });
    });
    setStyles({
      drawer: {
        transform: getDrawerTransform(adjustedDrag),
        transition: 'none'
      },
      mask: {
        opacity: getMaskOpacity(adjustedDrag),
        transition: 'none'
      }
    });
  };
  var handleSwiped = function handleSwiped(eventData) {
    if (!isSwipeEnabled(eventData)) {
      return resetStyles();
    }
    if (Date.now() - swipeStart.current > SWIPE_DURATION) {
      return resetStyles();
    }
    if (!canCloseDrawer.current) {
      canCloseDrawer.current = true;
      return resetStyles();
    }
    onSwiped();
    setStyles({
      drawer: {
        transform: getDrawerTransform(drag.drag)
      },
      mask: {
        opacity: getMaskOpacity(drag.drag)
      }
    });
    setDrag(function (prevDrag) {
      return _objectSpread(_objectSpread({}, prevDrag), {}, {
        drag: 0,
        isDown: false,
        finished: true,
        pointers: true
      });
    });
  };
  var _useSwipeable = useSwipeable({
      onSwipeStart: handleSwipeStart,
      onSwiping: handleSwiping,
      onSwiped: handleSwiped,
      enabled: enabled,
      availableDirections: [POSITION_TO_SWIPE_DIRECTION_MAP[position]],
      trackMouse: true
    }),
    ref = _useSwipeable.ref,
    swipeProps = _objectWithoutProperties(_useSwipeable, _excluded);
  return {
    swipeRef: mergeRefs(ref, swipeRef),
    drawerStyles: styles === null || styles === void 0 ? void 0 : styles.drawer,
    maskStyles: styles === null || styles === void 0 ? void 0 : styles.mask,
    showPointer: !drag.pointers,
    swipeProps: swipeProps,
    drawerMotionProps: {
      onLeaveActive: function onLeaveActive() {
        return setStyles(undefined);
      },
      onLeaveEnd: function onLeaveEnd() {
        return setStyles(undefined);
      }
    }
  };
}